import { VueGtag } from 'vue-gtag';
import { FeedType, ThemeTopic } from '@/types';
import { gaTracker } from '@/utils/ga';

const keyPrefix = 'UserSession';

export const userSession = {
  getUserId(): number | null {
    const value = localStorage.getItem(`${keyPrefix}.UserId`);
    if (value !== null) {
      return Number(value);
    }
    return null;
  },
  getTeamId(): number | null {
    const value = localStorage.getItem(`${keyPrefix}.TeamId`);
    if (value !== null) {
      return Number(value);
    }
    return null;
  },
  getTeamIdAsString(): string | null {
    const value = this.getTeamId();
    if (value === null) {
      return null;
    }
    return value.toString();
  },
  getFeedType(): FeedType {
    const isFeedType = (x: string): x is FeedType =>
      ['all', 'domestic', 'foreign'].includes(x);
    const gotFeedType = localStorage.getItem(`${keyPrefix}.FeedType`);
    if (gotFeedType === null || !isFeedType(gotFeedType)) {
      return 'domestic';
    }
    return gotFeedType;
  },
  setFeedType(feedType: string): void {
    localStorage.setItem(`${keyPrefix}.FeedType`, feedType);
  },
  getIsShownTranslateNotification(): boolean {
    return Boolean(
      parseInt(
        localStorage.getItem(`${keyPrefix}.IsShownTranslateNotification`) ??
          '0',
      ),
    );
  },
  setIsShownTranslateNotification(): void {
    localStorage.setItem(`${keyPrefix}.IsShownTranslateNotification`, '1');
  },
  getMarkNewsSelectedUserNames(): string[] {
    return JSON.parse(
      localStorage.getItem(`${keyPrefix}.MarkNewsSelectedUserNames`) ?? '[]',
    );
  },
  setMarkNewsSelectedUserNames(userIds: string[]): void {
    localStorage.setItem(
      `${keyPrefix}.MarkNewsSelectedUserNames`,
      JSON.stringify(userIds),
    );
  },
  start(userId: number, teamId: number, gtag: VueGtag) {
    if (!userId || !teamId) return;
    localStorage.setItem(`${keyPrefix}.UserId`, userId.toString());
    localStorage.setItem(`${keyPrefix}.TeamId`, teamId.toString());
    gaTracker.config(gtag);
  },
  isActive() {
    // TODO 本当はSession Cookieの存在有無を見るべきだが一旦LocalStorageを見る
    const user_id = localStorage.getItem(`${keyPrefix}.UserId`);
    const team_id = localStorage.getItem(`${keyPrefix}.TeamId`);
    return user_id !== null && team_id !== null;
  },
  clear(gtag: VueGtag) {
    Object.keys(localStorage).forEach(key => {
      localStorage.removeItem(key);
    });
    gaTracker.clearConfig(gtag);
  },
  setThemeListState(
    accessScope: string,
    id: number | null,
    isOpen: boolean,
  ): void {
    localStorage.setItem(
      `${accessScope}${id ? `-${id}` : ''}`,
      isOpen.toString(),
    );
  },
  getThemeListState(accessScope: string, id: number | null): boolean {
    const storedValue = localStorage.getItem(
      `${accessScope}${id ? `-${id}` : ''}`,
    );
    return storedValue ? storedValue === 'true' : true;
  },
  setLeftMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(`${keyPrefix}.LeftMenuIsExpanded`);
    return isExpanded ? isExpanded === 'true' : true;
  },
  /** 新レイアウトの左メニューのパネル幅。0は折りたたみ状態 */
  setLeftMenuPanelWidth(width: number): void {
    localStorage.setItem(`${keyPrefix}.LeftMenuPanelWidth`, width.toString());
  },
  getLeftMenuPanelWidth(): number | undefined {
    const width = localStorage.getItem(`${keyPrefix}.LeftMenuPanelWidth`);
    if (width === null) return undefined;
    const parsed = Number(width);
    return Number.isFinite(parsed) ? parsed : undefined;
  },
  /** FMEA の表の列幅。ワークシートで 1 組、システムをまたいで共通 */
  setFmeaTableColumnWidths(widths: Record<string, number>): void {
    localStorage.setItem(
      `${keyPrefix}.FmeaTableColumnWidths.sheet`,
      JSON.stringify(widths),
    );
  },
  getFmeaTableColumnWidths(): Record<string, number> | undefined {
    const stored = localStorage.getItem(
      `${keyPrefix}.FmeaTableColumnWidths.sheet`,
    );
    if (stored === null) return undefined;

    try {
      const parsed: unknown = JSON.parse(stored);
      if (typeof parsed !== 'object' || parsed === null) return undefined;

      return Object.fromEntries(
        Object.entries(parsed).filter(([, width]) => Number.isFinite(width)),
      );
    } catch {
      return undefined;
    }
  },
  setUseLegacyLayout(useLegacy: boolean): void {
    localStorage.setItem(`${keyPrefix}.UseLegacyLayout`, useLegacy.toString());
  },
  getUseLegacyLayout(): boolean {
    return localStorage.getItem(`${keyPrefix}.UseLegacyLayout`) === 'true';
  },
  setLeftReportMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftReportMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftReportMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftReportMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setLeftLabsMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftLabsMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftLabsMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftLabsMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setLeftPatentFtoMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftPatentFtoMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftPatentFtoMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftPatentFtoMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setLeftTechExplorationMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftTechExplorationMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftTechExplorationMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftTechExplorationMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setLeftForcedIdeationMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftForcedIdeationMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftForcedIdeationMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftForcedIdeationMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setLeftMarketSizingMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftMarketSizingMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftMarketSizingMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftMarketSizingMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setLeftFmeaMenuIsExpanded(isExpanded: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.LeftFmeaMenuIsExpanded`,
      isExpanded.toString(),
    );
  },
  getLeftFmeaMenuIsExpanded(): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.LeftFmeaMenuIsExpanded`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setSelectedThemeTopic(selectedThemeTopic: ThemeTopic): void {
    localStorage.setItem(
      `${keyPrefix}.SelectedThemeTopic`,
      JSON.stringify(selectedThemeTopic),
    );
  },
  getSelectedThemeTopic(): ThemeTopic | undefined {
    const selectedThemeTopic = localStorage.getItem(
      `${keyPrefix}.SelectedThemeTopic`,
    );
    return selectedThemeTopic ? JSON.parse(selectedThemeTopic) : undefined;
  },
  deleteSelectedThemeTopic() {
    localStorage.removeItem(`${keyPrefix}.SelectedThemeTopic`);
  },
  setShowThemeSupportContent(showThemeSupportContent: boolean): void {
    localStorage.setItem(
      `${keyPrefix}.ShowThemeSupportContent`,
      JSON.stringify(showThemeSupportContent),
    );
  },
  getShowThemeSupportContent(): boolean {
    const showThemeSupportContent = localStorage.getItem(
      `${keyPrefix}.ShowThemeSupportContent`,
    );
    return showThemeSupportContent ? showThemeSupportContent === 'true' : true;
  },
  clearTeamMarkSort() {
    const key = `${keyPrefix}.TeamMarkSort`;
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
    }
  },
  setIsOpenedFromAdpDocumentCardLink(
    isOpenedFromAdpDocumentCardLin: boolean,
  ): void {
    localStorage.setItem(
      `${keyPrefix}.IsOpenedFromAdpDocumentCardLink`,
      JSON.stringify(isOpenedFromAdpDocumentCardLin),
    );
  },
  getIsOpenedFromAdpDocumentCardLink(): boolean {
    const isOpenedFromAdpDocumentCardLin = localStorage.getItem(
      `${keyPrefix}.IsOpenedFromAdpDocumentCardLink`,
    );
    return isOpenedFromAdpDocumentCardLin
      ? isOpenedFromAdpDocumentCardLin === 'true'
      : false;
  },
  getLastUsedGroupId(): number | undefined {
    const lastUsedGroupId = localStorage.getItem(
      `${keyPrefix}.LastUsedGroupId`,
    );
    return lastUsedGroupId !== null ? Number(lastUsedGroupId) : undefined;
  },
  setLastUsedGroupId(groupId: number): void {
    localStorage.setItem(`${keyPrefix}.LastUsedGroupId`, String(groupId));
  },
  getFollowListCommentsVisible(): boolean {
    const followListCommentsVisible = localStorage.getItem(
      `${keyPrefix}.followListCommentsVisible`,
    );
    if (followListCommentsVisible === null) {
      return false;
    }
    // 文字列trueが入っている場合はtrue、それ以外はfalseとして扱う
    return followListCommentsVisible.toLowerCase() === 'true';
  },
  setFollowListCommentsVisible(isVisible: boolean): void {
    // localStorageにはbooleanをそのまま保存できないため、文字列に変換して保存
    localStorage.setItem(
      `${keyPrefix}.followListCommentsVisible`,
      String(isVisible),
    );
  },
  getLabsSideMenuFolderState(): Array<string> {
    const labsSideMenuFolderState = localStorage.getItem(
      `${keyPrefix}.LabsSideMenuFolderState`,
    );
    return labsSideMenuFolderState ? JSON.parse(labsSideMenuFolderState) : [];
  },
  setLabsSideMenuFolderState(labsSideMenuFolderState: Array<string>): void {
    localStorage.setItem(
      `${keyPrefix}.LabsSideMenuFolderState`,
      JSON.stringify(labsSideMenuFolderState),
    );
  },
  setSamlFederationAdminOperationCheckData(
    id: string,
    name: string,
    metadataUrl: string,
    idpEntityId: string,
    idpSsoServiceUrl: string,
  ): void {
    const data = {
      id,
      name,
      metadataUrl,
      idpEntityId,
      idpSsoServiceUrl,
    };
    localStorage.setItem(
      `${keyPrefix}.SamlFederationAdminOperationCheckData`,
      JSON.stringify(data),
    );
  },
  getSamlFederationAdminOperationCheckData(): {
    id: string;
    name: string;
    metadataUrl: string;
    idpEntityId: string;
    idpSsoServiceUrl: string;
  } {
    const emptyData = {
      id: '',
      name: '',
      metadataUrl: '',
      idpEntityId: '',
      idpSsoServiceUrl: '',
    };
    try {
      const parsedData = JSON.parse(
        localStorage.getItem(
          `${keyPrefix}.SamlFederationAdminOperationCheckData`,
        ) ?? JSON.stringify(emptyData),
      );
      return parsedData;
    } catch (_e) {
      return emptyData;
    }
  },
  clearSamlFederationAdminOperationCheckData(): void {
    localStorage.removeItem(
      `${keyPrefix}.SamlFederationAdminOperationCheckData`,
    );
  },
  setSurveyTemplatePromptCategoryIsExpanded(
    categoryId: string | number,
    isExpanded: boolean,
  ): void {
    localStorage.setItem(
      `${keyPrefix}.SurveyTemplatePromptCategoryIsExpanded.${categoryId}`,
      isExpanded.toString(),
    );
  },
  getSurveyTemplatePromptCategoryIsExpanded(
    categoryId: string | number,
  ): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.SurveyTemplatePromptCategoryIsExpanded.${categoryId}`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
  setSurveyMenuIsExpanded(
    listName: 'surveys' | 'shareSurveys',
    isExpanded: boolean,
  ): void {
    localStorage.setItem(
      `${keyPrefix}.SurveyMenuIsExpanded.${listName}`,
      isExpanded.toString(),
    );
  },
  getSurveyMenuIsExpanded(listName: 'surveys' | 'shareSurveys'): boolean {
    const isExpanded = localStorage.getItem(
      `${keyPrefix}.SurveyMenuIsExpanded.${listName}`,
    );
    return isExpanded ? isExpanded === 'true' : true;
  },
};
